import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatRupiah, formatNumber, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { SupplierName } from "@/components/SupplierName";

export default function PaymentSlip() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [supplier, setSupplier] = useState(null);

  useEffect(() => {
    api.get(`/schedules/${id}`).then((r) => {
      setData(r.data);
      api.get("/suppliers").then((sr) => setSupplier(sr.data.find((s) => s.id === r.data.schedule.supplier_id)));
    });
  }, [id]);

  if (!data) return <div className="p-8 text-slate-500">Memuat...</div>;
  const s = data.schedule;
  const invs = data.invoices;
  const trIdx = parseInt(searchParams.get("tranche") || "", 10);
  const singleTranche = s.is_split && !isNaN(trIdx) ? (s.tranches || [])[trIdx - 1] : null;
  const totalRolls = invs.reduce((a, i) => a + i.total_rolls, 0);

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <style>{`@media print { .no-print { display:none !important; } @page { margin: 1.5cm; } }`}</style>
      <div className="max-w-2xl mx-auto px-4 no-print flex justify-between mb-4">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="slip-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Kembali</Button>
        <Button onClick={() => window.print()} data-testid="slip-print-btn"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button>
      </div>

      <div className="max-w-2xl mx-auto bg-white shadow-sm print:shadow-none p-8" data-testid="slip-content">
        <div className="flex items-center justify-between border-b-2 border-slate-900 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <img src="/logo-meranggi.png" alt="Meranggi Garment" className="h-12 w-12 rounded-full object-cover" />
            <div>
              <div className="text-xl font-bold text-slate-900">Slip Pembayaran{singleTranche ? ` · Termin BG ${trIdx}` : ""}</div>
              <div className="text-sm text-slate-500">CV. Meranggi Jaya · Meranggi Garment</div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-sm font-semibold">{s.code}</div>
            {s.reschedule_badge && <div className="text-xs text-purple-600 font-semibold">RESCHEDULE</div>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm mb-6">
          <Field label="Supplier" value={<SupplierName name={supplier?.name || "-"} id={supplier?.id} />} />
          {singleTranche ? (
            <>
              <Field label="Jenis Pembayaran" value={`Totalan · Termin BG ${trIdx} (${singleTranche.method === "giro" ? "Bilyet Giro" : "Transfer"})`} />
              <Field label="Tanggal Pencairan" value={formatDate(singleTranche.cair_date)} />
              <Field label="Status" value={singleTranche.cleared ? "CAIR" : "TERJADWAL"} />
              {singleTranche.method === "giro" && <Field label="Nomor Warkat" value={singleTranche.warkat_number} />}
              {singleTranche.method === "giro" && <Field label="Bank Warkat" value={singleTranche.warkat_bank} />}
            </>
          ) : (
            <>
              <Field label="Jenis Pembayaran" value={s.method === "giro" ? "Bilyet Giro" : s.method === "split" ? "Totalan (beberapa BG)" : "Transfer"} />
              <Field label="Tanggal Pembayaran" value={formatDate(s.payment_date)} />
              <Field label="Status" value={s.status === "settled" ? "LUNAS" : "TERJADWAL"} />
              {s.method === "giro" && <Field label="Nomor Warkat" value={s.warkat_number} />}
              {s.method === "giro" && <Field label="Bank Warkat" value={s.warkat_bank} />}
            </>
          )}
        </div>

        {s.is_split && !singleTranche && (
          <table className="w-full text-sm border-collapse mb-6">
            <thead><tr className="bg-slate-100 text-left"><th className="p-2">Termin</th><th className="p-2">Metode</th><th className="p-2">Warkat/Bank</th><th className="p-2">Tgl Cair</th><th className="p-2 text-right">Nominal</th><th className="p-2">Status</th></tr></thead>
            <tbody>
              {(s.tranches || []).map((t, i) => (
                <tr key={i} className="border-b border-slate-200">
                  <td className="p-2">#{i + 1}</td>
                  <td className="p-2">{t.method === "giro" ? "Giro" : "Transfer"}</td>
                  <td className="p-2">{t.method === "giro" ? `${t.warkat_number} / ${t.warkat_bank}` : "-"}</td>
                  <td className="p-2">{formatDate(t.cair_date)}</td>
                  <td className="p-2 text-right font-mono">{formatRupiah(t.amount)}</td>
                  <td className="p-2">{t.cleared ? "Cair" : "Belum"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <table className="w-full text-sm border-collapse mb-6">
          <thead><tr className="bg-slate-100 text-left">
            <th className="p-2">No. Nota</th><th className="p-2">Tgl Nota</th>
            <th className="p-2 text-right">Roll</th><th className="p-2 text-right">Nilai</th>
          </tr></thead>
          <tbody>
            {invs.map((i) => (
              <tr key={i.id} className="border-b border-slate-200">
                <td className="p-2">{i.invoice_number}</td>
                <td className="p-2">{formatDate(i.invoice_date)}</td>
                <td className="p-2 text-right font-mono">{formatNumber(i.total_rolls)}</td>
                <td className="p-2 text-right font-mono">{formatRupiah(i.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-64 space-y-1 text-sm">
            <Row label="Total Roll" value={formatNumber(totalRolls)} />
            <Row label="Potongan Retur" value={`- ${formatRupiah(s.credit_used)}`} />
            {singleTranche && <Row label="Total Totalan" value={formatRupiah(s.net_amount)} />}
            <div className="flex justify-between border-t-2 border-slate-900 pt-2 mt-2 font-bold text-base">
              <span>{singleTranche ? `Nominal BG ${trIdx}` : "Pembayaran Bersih"}</span><span className="font-mono">{formatRupiah(singleTranche ? singleTranche.amount : s.net_amount)}</span>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-8 text-sm text-center">
          <div><div className="h-16 border-b border-slate-400"></div><div className="mt-1 text-slate-500">Disiapkan</div></div>
          <div><div className="h-16 border-b border-slate-400"></div><div className="mt-1 text-slate-500">Disetujui Owner</div></div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="font-medium text-slate-900">{value}</div></div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-mono">{value}</span></div>
);
